import { useForm } from 'react-hook-form';
import { CourseOutlineFormData, Module, QuizModule } from '../types';
import { useCallback } from 'react';

interface UseCourseOutlineFormProps {
  initialCourseTitle: string;
  initialDescription: string;
  initialModules: Module[];
  initialQuizModules: QuizModule[];
}

export const useCourseOutlineForm = ({
  initialCourseTitle,
  initialDescription,
  initialModules,
  initialQuizModules,
}: UseCourseOutlineFormProps) => {
  const createQuizDefaultValues = useCallback(() => {
    const defaultValues: any = {
      courseTitle: initialCourseTitle,
      courseDescription: initialDescription,
      modules: initialModules,
      quizModules: initialQuizModules,
    };

    initialQuizModules.forEach((quizModule, moduleIndex) => {
      quizModule.screens.forEach((screen, screenIndex) => {
        defaultValues[`questionText_${moduleIndex}_${screenIndex}`] = screen.question.text;
        defaultValues[`options_${moduleIndex}_${screenIndex}`] = screen.question.options;
        defaultValues[`points_${moduleIndex}_${screenIndex}`] = screen.question.points;
        if (screen.question.hint) {
          defaultValues[`hint_${moduleIndex}_${screenIndex}`] = screen.question.hint;
        }
      });
    });

    return defaultValues;
  }, [initialCourseTitle, initialModules, initialQuizModules, initialDescription]);

  // Main form for all course outline data
  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<CourseOutlineFormData>({
    defaultValues: createQuizDefaultValues(),
    mode: 'all',
  });

  // Section edit form
  const {
    control: sectionControl,
    handleSubmit: handleSectionSubmit,
    reset: resetSectionForm,
  } = useForm<{ courseTitle: string, courseDescription: string }>({
    defaultValues: {
      courseTitle: initialCourseTitle,
      courseDescription: initialDescription
    },
  });

  // Quiz edit form
  const {
    control: quizControl,
    handleSubmit: handleQuizSubmit,
    reset: resetQuizForm,
  } = useForm<{ title: string }>({
    defaultValues: {
      title: '',
    },
  });

  // Helper functions
  const updateModule = useCallback((moduleIndex: number, newTitle: string) => {
    const currentModules = getValues('modules');
    const updatedModules = [...currentModules];
    if (updatedModules[moduleIndex]) {
      updatedModules[moduleIndex] = {
        ...updatedModules[moduleIndex],
        title: newTitle,
      };
    }
    setValue('modules', updatedModules);
  }, [getValues, setValue]);

  const updateLesson = useCallback((moduleIndex: number, lessonIndex: number, updatedLesson: any) => {
    const currentModules = getValues('modules');
    const updatedModules = [...currentModules];
    const module = updatedModules[moduleIndex];
    if (module && module.lessons && module.lessons[lessonIndex]) {
      module.lessons[lessonIndex] = updatedLesson;
    }
    setValue('modules', updatedModules);
  }, [getValues, setValue]);

  const deleteModule = useCallback((moduleIndex: number) => {
    const currentModules = getValues('modules');
    const updatedModules = [...currentModules];
    updatedModules.splice(moduleIndex, 1);
    setValue('modules', updatedModules);
  }, [getValues, setValue]);

  const deleteLesson = useCallback((moduleIndex: number, lessonIndex: number) => {
    const currentModules = getValues('modules');
    const updatedModules = [...currentModules];
    const module = updatedModules[moduleIndex];
    if (module && module.lessons) {
      module.lessons.splice(lessonIndex, 1);
    }
    setValue('modules', updatedModules);
  }, [getValues, setValue]);

  const reorderModules = useCallback((sourceIndex: number, targetIndex: number) => {
    const currentModules = getValues('modules');
    const newModules = [...currentModules];
    const [removed] = newModules.splice(sourceIndex, 1);
    newModules.splice(targetIndex, 0, removed);
    setValue('modules', newModules);
  }, [getValues, setValue]);

  const updateCourseTitle = useCallback((newTitle: string) => {
    setValue('courseTitle', newTitle);
  }, [setValue]);
  const updateCourseDescription = useCallback((newTitle: string) => {
    setValue('courseDescription', newTitle);
  }, [setValue]);



  // Quiz module management functions
  const updateQuizModule = useCallback((quizIndex: number, newTitle: string) => {
    const currentQuizModules = getValues('quizModules');
    const updatedQuizModules = [...currentQuizModules];
    if (updatedQuizModules[quizIndex]) {
      updatedQuizModules[quizIndex] = {
        ...updatedQuizModules[quizIndex],
        title: newTitle,
      };
    }
    setValue('quizModules', updatedQuizModules);
  }, [getValues, setValue]);

  const deleteQuizModule = useCallback((quizIndex: number) => {
    const currentQuizModules = getValues('quizModules');
    const updatedQuizModules = [...currentQuizModules];
    updatedQuizModules.splice(quizIndex, 1);
    setValue('quizModules', updatedQuizModules);
  }, [getValues, setValue]);

  return {
    control,
    sectionControl,
    quizControl,
    handleSectionSubmit,
    handleQuizSubmit,
    resetSectionForm,
    resetQuizForm,
    updateModule,
    updateLesson,
    deleteModule,
    deleteLesson,
    reorderModules,
    updateCourseTitle,
    updateCourseDescription,
    updateQuizModule,
    deleteQuizModule,
    watch,
    getValues,
    setValue,
    reset,
    errors,
  };
};
